import { useEffect, useMemo, useState } from "react";
import { getDisplayCode } from "@/lib/display-code";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Trophy,
  Target,
  Crosshair,
  Hash,
  Sparkles,
  Lock,
  Loader2,
  List,
  GitBranch,
  Check,
  X,
  Minus,
  Clock,
  Printer,
  Skull,
} from "lucide-react";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import {
  getMemberBracketView,
  matchCodeToFrontId,
  type MemberBracketView,
} from "@/lib/bracket.functions";
import {
  getEntryEngagement,
  type EntryEngagement,
} from "@/lib/entry-engagement.functions";
import { useLeague } from "@/lib/league-context";
import { initialMatches, progressionMap, loserProgressionMap } from "@/lib/bracket-data";
import { BracketTreeView, type PickResult, type ActualMatchResult } from "@/components/BracketTreeView";
import type { Match } from "@/components/MatchCard";
import { Flag } from "@/components/Flag";
import { RankBadge } from "@/components/RankBadge";
import { FifaRankingLegend } from "@/components/FifaRankingLegend";
import { MemberGroupPicksSection } from "@/components/MemberGroupPicksSection";
import { EntryEngagementStrip } from "@/components/EntryEngagementStrip";
import { PeerViewHeader } from "@/components/PeerViewHeader";
import { PersonalRoastCard } from "@/components/PersonalRoastCard";
import { LeagueNav } from "@/components/LeagueNav";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/$league/members/$entryId")({
  component: BracketDetailPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: "bracket" | "groups" | undefined } => {
    const raw = search.tab;
    // Keep undefined when the URL doesn't pin a tab so the component can
    // pick a sensible default based on league state (G+K leagues default
    // to Groups until R32 has been seeded — knockoutPickable).
    const tab: "bracket" | "groups" | undefined =
      raw === "groups" ? "groups" : raw === "bracket" ? "bracket" : undefined;
    return { tab };
  },
  head: () => ({
    meta: [
      { title: "Bracket — FIFA 2026" },
      {
        name: "description",
        content:
          "View another player's locked-in bracket: every pick, the running score, and bonus answers.",
      },
    ],
  }),
});

// =================================================================
// Map server scoring statuses to the tree's color/result type.
// Server returns: "earned" | "void" | "missed" | "pending" | "not_picked"
// Tree expects:   "correct" | "incorrect" | "pending" | "void"
// =================================================================
function statusToPickResult(
  status: "earned" | "void" | "missed" | "pending" | "not_picked",
): PickResult {
  switch (status) {
    case "earned":
      return "correct";
    case "missed":
      return "incorrect";
    case "void":
      return "void";
    case "pending":
    case "not_picked":
    default:
      return "pending";
  }
}

// Hydrate Match[] from real picks. R32 home/away come from
// `tournament_matches` (admin-set seeding); later-round slots fill in via
// progressionMap as picks cascade forward.
function hydrateMatchesFromPicks(
  picks: MemberBracketView["picks"],
  teams: MemberBracketView["teams"],
  tournamentMatches: MemberBracketView["matches"],
): Match[] {
  const idToCode = new Map(teams.map((t) => [t.id, t.code]));
  const idToTeam = new Map(
    teams.map((t) => [t.id, { name: t.name, code: t.code, flag: "" }]),
  );

  // R32 seeding: match_code (DB canonical e.g. "R32-1") -> home/away team ids
  const r32SeedingByFrontId = new Map<
    string,
    { teamA: { name: string; code: string; flag: string } | null; teamB: { name: string; code: string; flag: string } | null }
  >();
  for (const m of tournamentMatches) {
    if (m.round !== "r32") continue;
    r32SeedingByFrontId.set(matchCodeToFrontId(m.match_code), {
      teamA: m.home_team_id ? idToTeam.get(m.home_team_id) ?? null : null,
      teamB: m.away_team_id ? idToTeam.get(m.away_team_id) ?? null : null,
    });
  }

  const pickByFrontId = new Map<
    string,
    { teamCode: string | null; goals: number | null }
  >();
  for (const p of picks) {
    pickByFrontId.set(matchCodeToFrontId(p.match_code), {
      teamCode: p.picked_team_id ? idToCode.get(p.picked_team_id) ?? null : null,
      goals: p.predicted_total_goals,
    });
  }

  // Start from the structure-only template and apply admin R32 seeding.
  const draft: Match[] = initialMatches.map((m) => {
    const seed = r32SeedingByFrontId.get(m.id);
    return {
      ...m,
      teamA: seed?.teamA ?? null,
      teamB: seed?.teamB ?? null,
      winner: null,
      predictedGoals: "",
    };
  });
  const byId = new Map(draft.map((m) => [m.id, m]));

  // Apply picks in round order so later-round teamA/teamB slots can be
  // populated by earlier picks via the progression map.
  const ROUND_PRIORITY: Record<string, number> = {
    "Round of 32": 1,
    "Round of 16": 2,
    "Quarter Final": 3,
    "Semi Final": 4,
    "Third Place": 5,
    Final: 6,
  };
  const ordered = [...draft].sort(
    (a, b) =>
      (ROUND_PRIORITY[a.round] ?? 99) - (ROUND_PRIORITY[b.round] ?? 99),
  );

  for (const m of ordered) {
    const cur = byId.get(m.id)!;
    const saved = pickByFrontId.get(m.id);
    if (saved?.goals != null) cur.predictedGoals = String(saved.goals);
    if (saved?.teamCode) {
      if (cur.teamA?.code === saved.teamCode) cur.winner = "A";
      else if (cur.teamB?.code === saved.teamCode) cur.winner = "B";
    }

    // Forward winner/loser into downstream slots
    const winnerTeam =
      cur.winner === "A" ? cur.teamA : cur.winner === "B" ? cur.teamB : null;
    const loserTeam =
      cur.winner === "A" ? cur.teamB : cur.winner === "B" ? cur.teamA : null;
    const winLink = progressionMap[m.id];
    if (winLink && winnerTeam) {
      const target = byId.get(winLink.target);
      if (target) {
        if (winLink.slot === "A") target.teamA = winnerTeam;
        else target.teamB = winnerTeam;
      }
    }
    const loseLink = loserProgressionMap[m.id];
    if (loseLink && loserTeam) {
      const target = byId.get(loseLink.target);
      if (target) {
        if (loseLink.slot === "A") target.teamA = loserTeam;
        else target.teamB = loserTeam;
      }
    }
  }

  return draft;
}

function BracketDetailPage() {
  const { league: leagueSlug, entryId } = Route.useParams();
  const { tab: tabParam } = Route.useSearch();
  const leagueCtx = useLeague();
  const navigate = useNavigate();
  const fn = useAuthedServerFn(getMemberBracketView);
  const engagementFn = useAuthedServerFn(getEntryEngagement);

  // Default tab logic: in G+K leagues, until admin has seeded R32
  // (knockoutPickable), the Knockout tab is a dead-end view with no
  // teams. Default newcomers to the Groups tab in that window. Once R32
  // is seeded — or the league is knockout-only — Knockout is the default.
  const isGK =
    leagueCtx?.tournamentType === "fifa_wc_mens_group_knockout";
  const defaultTab: "bracket" | "groups" =
    isGK && !leagueCtx?.knockoutPickable ? "groups" : "bracket";
  const tab: "bracket" | "groups" = tabParam ?? defaultTab;

  const [data, setData] = useState<MemberBracketView | null>(null);
  const [engagement, setEngagement] = useState<EntryEngagement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [picksView, setPicksView] = useState<"tree" | "list">("tree");

  useEffect(() => {
    if (!leagueCtx?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEngagement(null);
    fn({ data: { leagueId: leagueCtx.id, targetEntryId: entryId } })
      .then((res) => {
        if (cancelled) return;
        // Pre-lock, if the viewer owns this entry, drop them into their
        // own bracket editor instead of the peer-view chrome — avoids a
        // confusing "viewing your own bracket" read-only mode while picks
        // are still editable. Post-lock there's nothing to edit, so let
        // self users stay here (and /bracket actually redirects back here
        // post-lock — bouncing would cause an infinite loop).
        const isPostLock =
          !!leagueCtx?.bracketLockAt &&
          new Date(leagueCtx.bracketLockAt) <= new Date();
        // If viewer landed here with tab=groups (e.g. G+K post-group-lock
        // redirect from /groups), keep them here so they can actually see
        // their group picks — bouncing to /bracket would drop the tab and
        // strand them on Knockout with no way back to Groups.
        if (res.target.isSelf && !isPostLock && tab !== "groups") {
          const isGk = leagueCtx?.tournamentType === "fifa_wc_mens_group_knockout";
          const nothingToPickInKnockout = isGk && !leagueCtx?.knockoutPickable;
          void navigate({
            to: nothingToPickInKnockout ? "/$league/groups" : "/$league/bracket",
            params: { league: leagueSlug },
            search: { entry: entryId },
            replace: true,
          });
          return;
        }

        setData(res);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load bracket");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    engagementFn({ data: { leagueId: leagueCtx.id, entryId } })
      .then((res) => {
        if (!cancelled) setEngagement(res);
      })
      .catch(() => {
        // Engagement is best-effort; ignore errors to keep page usable.
      });
    return () => {
      cancelled = true;
    };
  }, [leagueCtx?.id, entryId, fn, engagementFn, navigate, leagueSlug, tab]);


  // ----- Derived data (must run every render; safe when `data` is null) -----
  const matches = useMemo(
    () =>
      data ? hydrateMatchesFromPicks(data.picks, data.teams, data.matches) : [],
    [data],
  );

  // Set of team codes that have been eliminated in the actual tournament.
  // Derived from played matches in `data.matches`: in any match with a
  // winner_team_id set, the OTHER team is eliminated. Used to flag a user's
  // pending knockout picks whose chosen team is already out.
  const eliminatedTeamCodes = useMemo<Set<string>>(() => {
    if (!data) return new Set();
    const idToCode = new Map(data.teams.map((t) => [t.id, t.code]));
    const out = new Set<string>();
    for (const m of data.matches) {
      if (!m.winner_team_id) continue;
      const loserId =
        m.home_team_id && m.home_team_id !== m.winner_team_id
          ? m.home_team_id
          : m.away_team_id && m.away_team_id !== m.winner_team_id
            ? m.away_team_id
            : null;
      const code = loserId ? idToCode.get(loserId) : null;
      if (code) out.add(code);
    }
    return out;
  }, [data]);

  // matchCode (DB canonical) -> PickResult for the tree.
  // Overlay: only upgrade to "busted" (whole-match red treatment) when the
  // user's picked WINNER for this slot has been eliminated upstream. If only
  // the predicted loser/opponent is out, the slot stays live because the
  // champion path could still happen (option B / winner-only scoring).
  // Dead-team styling on the loser row is handled separately via
  // `eliminatedTeamCodes` passed straight into BracketTreeView.
  const pickResults = useMemo<Record<string, PickResult>>(() => {
    if (!data) return {};
    const out: Record<string, PickResult> = {};
    for (const b of data.bracketScore.breakdown) {
      out[matchCodeToFrontId(b.match_code)] = statusToPickResult(b.status);
    }
    for (const m of matches) {
      const current = out[m.id];
      if (current && current !== "pending" && current !== "void") continue;
      const picked = m.winner === "A" ? m.teamA : m.winner === "B" ? m.teamB : null;
      if (picked && eliminatedTeamCodes.has(picked.code)) {
        out[m.id] = "busted";
      }
    }
    return out;
  }, [data, matches, eliminatedTeamCodes]);

  // Real-tournament results per match, keyed by front match id. Used by the
  // tree view to color goal predictions vs actual + render a small
  // "Actual: BRA 2 – ARG 1" footer once a match has been played.
  const actualResults = useMemo<Record<string, ActualMatchResult>>(() => {
    if (!data) return {};
    const idToCode = new Map(data.teams.map((t) => [t.id, t.code]));
    const out: Record<string, ActualMatchResult> = {};
    for (const m of data.matches) {
      if (!m.winner_team_id) continue;
      out[matchCodeToFrontId(m.match_code)] = {
        winnerTeamCode: idToCode.get(m.winner_team_id) ?? null,
        totalGoals: m.total_goals,
        homeTeamCode: m.home_team_id ? idToCode.get(m.home_team_id) ?? null : null,
        awayTeamCode: m.away_team_id ? idToCode.get(m.away_team_id) ?? null : null,
        homeScore: m.home_score ?? null,
        awayScore: m.away_score ?? null,
      };
    }
    return out;
  }, [data]);

  const finalMatch = matches.find((m) => m.id === "final");
  const champion =
    finalMatch?.winner === "A"
      ? finalMatch.teamA
      : finalMatch?.winner === "B"
        ? finalMatch.teamB
        : null;
  const runnerUp =
    finalMatch?.winner === "A"
      ? finalMatch.teamB
      : finalMatch?.winner === "B"
        ? finalMatch.teamA
        : null;

  // ----- Loading / error / not-found states -----
  if (loading || !leagueCtx) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-16">
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading bracket…
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-bold text-foreground">
          Bracket unavailable
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">{error}</p>
        <Link
          to="/$league"
          params={{ league: leagueSlug }}
          className="mt-6 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to leaderboard
        </Link>
      </main>
    );
  }

  if (!data) return null;

  const score = data.bracketScore;
  const picksWithWinner = matches.filter((m) => m.winner !== null).length;
  const picksWithGoals = matches.filter((m) => m.predictedGoals !== "").length;
  const TOTAL_MATCHES = matches.length; // 32

  // Bonus pick lookup
  const bonusByQuestion = new Map(
    data.bonusPicks.map((p) => [p.league_bonus_question_id, p.pick_value]),
  );

  const isLocked =
    !!leagueCtx?.bracketLockAt && new Date(leagueCtx.bracketLockAt) <= new Date();

  const compareUnlocked =
    !!leagueCtx?.bracketLockAt &&
    new Date(leagueCtx.bracketLockAt).getTime() <= Date.now();

  return (
    <>
      {data.target.isSelf ? (
        <LeagueNav />
      ) : (
        <PeerViewHeader
          entryId={entryId}
          displayName={data.target.displayName}
          ownerUserId={data.target.userId}
          compareUnlocked={compareUnlocked}
          isSelf={data.target.isSelf}
        />
      )}
      <main className="mx-auto max-w-5xl px-4 py-6 sm:py-8">

      {/* Inline print action — header chrome is rendered by PeerViewHeader in the layout. */}
      {isLocked && (
        <div className="mb-4 flex justify-end">
          <a
            href={`/print/${leagueSlug}/bracket/${entryId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/50 hover:text-primary"
          >
            <Printer className="h-3.5 w-3.5" />
            Print bracket
          </a>
        </div>
      )}


      {/* Header */}
      <header className="mb-6 sm:mb-8">
        <div className="flex flex-wrap items-start gap-4">
          {data.rank != null && (
            <div
              className={cn(
                "flex h-14 w-14 shrink-0 items-center justify-center rounded-xl font-display text-2xl font-bold sm:h-16 sm:w-16 sm:text-3xl",
                data.rank === 1
                  ? "bg-primary text-primary-foreground shadow-[var(--shadow-neon)]"
                  : data.rank <= 3
                    ? "border border-primary/40 bg-secondary text-foreground"
                    : "bg-muted text-muted-foreground",
              )}
            >
              {data.rank}
            </div>
          )}
          <div className="min-w-0 flex-1">
            {data.rank != null && (
              <div className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                Rank #{data.rank} of {data.totalMembers}
              </div>
            )}
            {(() => {
              const bracket = data.target.bracketName || data.target.displayName;
              const isProxy = data.target.entryType === "for_someone_else";
              // For proxy entries the "owner line" should name the player the
              // bracket is FOR (displayName is already "Noah (Brother)"), and
              // the secondary line is the server-built "Managed by …".
              // For self entries we surface the viewer's chosen identity:
              // real name when the league toggle is on, otherwise the
              // per-league display nickname — suppressed if it duplicates
              // the bracket name.
              const ownerLabel = isProxy
                ? data.target.displayName
                : (data.target.realName || data.target.displayName);
              const showOwner = !!ownerLabel && ownerLabel !== bracket;
              const secondary =
                data.target.secondaryLabel &&
                data.target.secondaryLabel !== bracket
                  ? data.target.secondaryLabel
                  : null;
              return (
                <>
                  <h1 className="font-display text-2xl font-bold leading-tight text-foreground sm:text-4xl">
                    {bracket}
                  </h1>
                  {showOwner && (
                    <p className="mt-1 text-sm text-muted-foreground sm:text-base">
                      {ownerLabel}
                    </p>
                  )}
                  {secondary && (
                    <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
                      {secondary}
                    </p>
                  )}
                </>
              );
            })()}
          </div>
          <div className="shrink-0 text-right">
            {(() => {
              const t = data.entryTotals;
              const big = t ? t.totalPoints : score.bracketPoints;
              const poss = t ? t.totalPossiblePoints : score.bracketPossiblePoints;
              const showSplit = !!t && t.isGroupKnockout;
              return (
                <>
                  <div className="font-display text-3xl font-bold leading-none text-primary sm:text-5xl">
                    {big}
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground sm:text-xs">
                    points
                  </div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground sm:text-xs">
                    of {poss} possible
                  </div>
                  {showSplit && (
                    <div className="mt-1 text-[10px] text-muted-foreground sm:text-xs">
                      Groups {t!.groupPoints}
                      <span className="text-muted-foreground/60">
                        /{t!.groupPossiblePoints}
                      </span>
                      <span className="mx-1 text-muted-foreground/60">·</span>
                      Knockout {t!.bracketPoints + t!.bonusPoints}
                      <span className="text-muted-foreground/60">
                        /{t!.bracketPossiblePoints + t!.bonusPossiblePoints}
                      </span>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {engagement && (
          <EntryEngagementStrip
            engagement={engagement}
            leagueSlug={leagueSlug}
          />
        )}

        {data.knockoutVisible && (
          <PersonalRoastCard
            entryId={data.target.entryId}
            postLock={data.knockoutVisible}
          />
        )}

        {/* Knockout stat strip — only on the Knockout tab. The Groups tab
            has its own group-relevant summary strip rendered inside
            MemberGroupPicksSection. */}
        {data.knockoutVisible && tab !== "groups" && (
          <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
            <Stat
              icon={<Target className="h-3.5 w-3.5" />}
              label="Match picks"
              value={`${picksWithWinner}`}
              sub={`of ${TOTAL_MATCHES}`}
            />
            <Stat
              icon={<Crosshair className="h-3.5 w-3.5" />}
              label="Goal picks"
              value={`${picksWithGoals}`}
              sub={`of ${TOTAL_MATCHES}`}
            />
            <Stat
              icon={<Trophy className="h-3.5 w-3.5" />}
              label="Earned"
              value={`${score.bracketPoints}`}
              sub="points"
            />
            <Stat
              icon={<Hash className="h-3.5 w-3.5" />}
              label="Tiebreaker"
              value={
                data.championTotalKnockoutGoals != null
                  ? String(data.championTotalKnockoutGoals)
                  : "—"
              }
              sub={
                data.finalizedChampion
                  ? `actual ${data.finalizedChampion.actualKnockoutGoals}${
                      data.championTotalKnockoutGoals != null
                        ? ` · off by ${Math.abs(data.championTotalKnockoutGoals - data.finalizedChampion.actualKnockoutGoals)}`
                        : ""
                    }`
                  : data.predictedChampionTeamId
                    ? `pick has ${data.predictedChampionActualGoals} so far`
                    : "champion goals"
              }
            />
          </div>
        )}
      </header>

      {/* Knockout-hidden banner — peer view in G+K leagues between group lock
          and bracket lock. Group picks are visible below; knockout picks stay
          private until bracket lock passes. */}
      {!data.knockoutVisible && (
        <div className="mb-6 rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          <div className="flex items-start gap-2">
            <Lock className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <span className="font-semibold text-foreground">
                Knockout picks are still hidden.
              </span>{" "}
              Group picks are locked and visible below. Bracket picks, bonus
              answers, and the tiebreaker unlock for everyone when the bracket
              lock timer hits zero.
            </div>
          </div>
        </div>
      )}

      {(() => {
        const isGK = data.league.tournamentType === "fifa_wc_mens_group_knockout";

        const groupsSection = isGK ? (
          <MemberGroupPicksSection
            leagueId={data.league.id}
            targetUserId={data.target.userId}
            entryId={data.target.entryId}
            isSelf={data.target.isSelf}
          />
        ) : null;

        const knockoutSections = data.knockoutVisible ? (
          <>
            {/* Finals Prediction Hero */}
            <section className="mb-8">
              <SectionHeading icon={<Sparkles className="h-4 w-4" />} title="Finals Prediction" />
              <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-6 sm:p-8">
                {champion && runnerUp ? (
                  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 sm:gap-6">
                    <div className="text-center sm:text-right">
                      <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-primary sm:text-xs">
                        Champion
                      </div>
                      <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end sm:gap-3">
                        <Flag code={champion.code} emoji={champion.flag} size={32} />
                        <span className="font-display text-xl font-bold text-foreground sm:text-3xl">
                          {champion.name}
                        </span>
                        <RankBadge code={champion.code} size="md" />
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="font-display text-xs font-black uppercase tracking-widest text-muted-foreground sm:text-sm">
                        defeats
                      </div>
                      {finalMatch?.predictedGoals && (
                        <div className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/10 px-2.5 py-1">
                          <span className="font-display text-sm font-black text-primary sm:text-base">
                            {finalMatch.predictedGoals}
                          </span>
                          <span className="font-mono text-[9px] uppercase tracking-widest text-primary/90 sm:text-[10px]">
                            total goals
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="text-center sm:text-left">
                      <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground sm:text-xs">
                        Runner-up
                      </div>
                      <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start sm:gap-3">
                        <Flag code={runnerUp.code} emoji={runnerUp.flag} size={32} />
                        <span className="font-display text-xl font-bold text-foreground sm:text-3xl">
                          {runnerUp.name}
                        </span>
                        <RankBadge code={runnerUp.code} size="md" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-sm italic text-muted-foreground">
                    No final predicted.
                  </p>
                )}
              </div>
              <FifaRankingLegend className="mt-3 text-center" />
            </section>

            {champion && (
              <ChampionPathWidget
                matches={matches}
                champion={champion}
                pickResults={pickResults}
              />
            )}

            {/* Busted-downstream alert */}
            <BustedPicksAlert
              matches={matches}
              breakdown={score.breakdown}
              eliminatedTeamCodes={eliminatedTeamCodes}
            />

            {/* Bracket picks */}
            <section className="mb-8">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <SectionHeading icon={<Trophy className="h-4 w-4" />} title="Bracket Picks" />
                <div className="inline-flex items-center rounded-lg border border-border bg-card p-0.5">
                  <ViewToggleButton
                    active={picksView === "tree"}
                    onClick={() => setPicksView("tree")}
                    icon={<GitBranch className="h-3.5 w-3.5" />}
                    label="Tree"
                  />
                  <ViewToggleButton
                    active={picksView === "list"}
                    onClick={() => setPicksView("list")}
                    icon={<List className="h-3.5 w-3.5" />}
                    label="List"
                  />
                </div>
              </div>
              <p className="mb-2 text-xs text-muted-foreground">
                <span className="font-semibold text-success">Green</span> = correct,{" "}
                <span className="font-semibold text-destructive">red</span> = incorrect, or your picked winner has been eliminated (whole match goes red 🪦). Eliminated teams anywhere in your bracket are crossed off in red — even if they were just the opponent. As long as your picked winner is still alive, the slot stays live.{" "}
                <span className="font-semibold text-muted-foreground">grey</span> = not played yet.
              </p>
              <p className="mb-4 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">Goal-total bonus:</span> only awarded when the winner pick is also correct.
              </p>

              {picksView === "tree" ? (
                <BracketTreeView
                  matches={matches}
                  readOnly
                  pickResults={pickResults}
                  actualResults={actualResults}
                  eliminatedTeamCodes={eliminatedTeamCodes}
                />
              ) : (
                <BracketPicksList
                  matches={matches}
                  breakdown={score.breakdown}
                  eliminatedTeamCodes={eliminatedTeamCodes}
                />
              )}
            </section>

            {/* Bonus question picks — hidden until knockout lock hits, since
                bonus picks are tied to the knockout phase. */}
            {isLocked && data.bonusQuestions.length > 0 && (
              <section className="mb-8">
                <SectionHeading
                  icon={<Sparkles className="h-4 w-4" />}
                  title="Bonus Question Picks"
                />
                <div className="grid gap-2.5 sm:grid-cols-2">
                  {data.bonusQuestions.map((q) => {
                    const pick = bonusByQuestion.get(q.id) ?? null;
                    const label =
                      pick === "over"
                        ? q.over_label ?? "Over"
                        : pick === "under"
                          ? q.under_label ?? "Under"
                          : pick;
                    const stat = q.derived_from_stat;
                    const threshold = q.threshold;
                    const currentValue =
                      stat && threshold != null
                        ? data.bonusStats?.[stat] ?? 0
                        : null;
                    const reached =
                      currentValue != null && threshold != null
                        ? currentValue >= threshold
                        : null;
                    let countTone = "text-muted-foreground";
                    if (pick && reached !== null) {
                      if (pick === "over") {
                        countTone = reached ? "text-success" : "text-muted-foreground";
                      } else if (pick === "under") {
                        countTone = reached ? "text-destructive" : "text-success";
                      }
                    }
                    return (
                      <div
                        key={q.id}
                        className="rounded-lg border border-border bg-card px-3 py-2.5"
                      >
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                          {q.short_label ?? q.question_label}
                        </div>
                        <div
                          className={cn(
                            "mt-0.5 text-sm font-semibold",
                            pick ? "text-foreground" : "text-muted-foreground italic",
                          )}
                        >
                          {label ?? "No pick"}
                        </div>
                        {currentValue != null && threshold != null && (
                          <div className={cn("mt-1 text-xs font-medium tabular-nums", countTone)}>
                            Now: {currentValue} <span className="text-muted-foreground">/ {threshold} to hit over</span>
                          </div>
                        )}
                      </div>
                    );
                  })}

                </div>
              </section>
            )}
          </>
        ) : null;

        // For non-G+K leagues (knockout-only), there is no Groups tab —
        // always render knockout sections regardless of `tab`.
        // For G+K leagues, switch by `tab`:
        //   - groups: render only group picks (with its own summary strip)
        //   - bracket: render knockout sections
        // Pre-knockout-visible in G+K, knockoutSections is null and the
        // viewer page already shows the "knockout picks still hidden" banner
        // above; in that state we still let the user see group picks.
        if (!isGK) {
          return knockoutSections;
        }
        if (tab === "groups") {
          return groupsSection;
        }
        // tab === "bracket" (default): show knockout if visible, otherwise
        // fall back to groups so the page isn't empty pre-bracket-lock.
        // Once the knockout is locked, append the group stage below so the
        // bracket tab is a holistic view of everything the player picked.
        if (knockoutSections) {
          // Pre-knockout-lock: group picks are the live story, so surface
          // them above finals/bracket. Once knockout locks, flip the order
          // back so the bracket headline leads.
          if (!isLocked && groupsSection) {
            return (
              <>
                {groupsSection}
                <section className="mt-8">{knockoutSections}</section>
              </>
            );
          }
          return (
            <>
              {knockoutSections}
              {groupsSection && (
                <section className="mt-8">{groupsSection}</section>
              )}
            </>
          );
        }
        return groupsSection;
      })()}
    </main>
    </>
  );
}

// =================================================================
// Sub-components
// =================================================================

function SectionHeading({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/15 text-primary">
        {icon}
      </div>
      <h2 className="font-display text-base font-bold text-foreground sm:text-lg">
        {title}
      </h2>
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/40 px-3 py-2.5">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground sm:text-xs">
        {icon}
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="font-display text-lg font-bold text-foreground sm:text-xl">
          {value}
        </span>
        {sub && (
          <span className="text-[10px] text-muted-foreground sm:text-xs">{sub}</span>
        )}
      </div>
    </div>
  );
}

function ViewToggleButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

const ROUND_ORDER = [
  "Round of 32",
  "Round of 16",
  "Quarter Final",
  "Semi Final",
  "Third Place",
  "Final",
];

function BracketPicksList({
  matches,
  breakdown,
  eliminatedTeamCodes,
}: {
  matches: Match[];
  breakdown: MemberBracketView["bracketScore"]["breakdown"];
  eliminatedTeamCodes: Set<string>;
}) {
  const breakdownByFrontId = new Map(
    breakdown.map((b) => [matchCodeToFrontId(b.match_code), b]),
  );

  return (
    <div className="space-y-5">
      {ROUND_ORDER.map((round) => {
        const items = matches.filter((m) => m.round === round);
        if (items.length === 0) return null;
        return (
          <div key={round}>
            <div className="mb-2 flex items-center gap-3">
              <h3 className="font-display text-xs font-black uppercase tracking-[0.25em] text-primary">
                {round === "Third Place" ? "Bronze · 3rd Place" : round}
              </h3>
              <div className="h-px flex-1 bg-gradient-to-r from-primary/40 to-transparent" />
              <span className="font-mono text-[10px] text-muted-foreground">
                {items.length} {items.length === 1 ? "match" : "matches"}
              </span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {items.map((m) => (
                <PickRow
                  key={m.id}
                  match={m}
                  status={breakdownByFrontId.get(m.id)?.status ?? "not_picked"}
                  pointsEarned={breakdownByFrontId.get(m.id)?.pointsEarned ?? 0}
                  pointsPossible={breakdownByFrontId.get(m.id)?.pointsPossible ?? 0}
                  eliminatedTeamCodes={eliminatedTeamCodes}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PickRow({
  match,
  status,
  pointsEarned,
  pointsPossible,
  eliminatedTeamCodes,
}: {
  match: Match;
  status: "earned" | "void" | "missed" | "pending" | "not_picked";
  pointsEarned: number;
  pointsPossible: number;
  eliminatedTeamCodes: Set<string>;
}) {
  const winnerTeam =
    match.winner === "A" ? match.teamA : match.winner === "B" ? match.teamB : null;
  const loserTeam =
    match.winner === "A" ? match.teamB : match.winner === "B" ? match.teamA : null;


  // "Busted-downstream" provisional flag: pick is still pending in our
  // scoring (its match hasn't been played yet) but the team they picked to
  // WIN has already been eliminated in the real tournament. The pick is
  // going to score 0 — we surface it now (option B / winner-only scoring).
  const bustedDownstream =
    (status === "pending" || status === "void") &&
    !!winnerTeam &&
    eliminatedTeamCodes.has(winnerTeam.code);
  // Loser side eliminated but winner still alive: highlight the dead team
  // only — slot remains live for scoring.
  const loserDead =
    !bustedDownstream && !!loserTeam && eliminatedTeamCodes.has(loserTeam.code);

  const styles = (() => {
    if (bustedDownstream) {
      return {
        border: "border-destructive/40",
        bg: "bg-destructive/5",
        text: "text-destructive",
        icon: <X className="h-3 w-3" strokeWidth={3} />,
        label: "Busted",
      };
    }
    switch (status) {
      case "earned":
        return {
          border: "border-success/40",
          bg: "bg-success/5",
          text: "text-success",
          icon: <Check className="h-3 w-3" strokeWidth={3} />,
          label: "Correct",
        };
      case "missed":
        return {
          border: "border-destructive/40",
          bg: "bg-destructive/5",
          text: "text-destructive",
          icon: <X className="h-3 w-3" strokeWidth={3} />,
          label: "Wrong",
        };
      case "void":
        return {
          border: "border-muted-foreground/30",
          bg: "bg-muted/20",
          text: "text-muted-foreground",
          icon: <Minus className="h-3 w-3" />,
          label: "Void",
        };
      case "pending":
        return {
          border: "border-border",
          bg: "bg-card",
          text: "text-foreground",
          icon: <Clock className="h-3 w-3" />,
          label: "Pending",
        };
      case "not_picked":
      default:
        return {
          border: "border-dashed border-muted-foreground/30",
          bg: "bg-muted/10",
          text: "text-muted-foreground",
          icon: <Minus className="h-3 w-3" />,
          label: "No pick",
        };
    }
  })();

  return (
    <div className={cn("rounded-lg border px-3 py-2.5", styles.border, styles.bg)}>
      <div className="mb-1.5 flex items-center gap-2">
        <span className={cn("inline-flex items-center gap-1", styles.text)}>
          {styles.icon}
          <span className="text-[10px] font-semibold uppercase tracking-wide">
            {styles.label}
          </span>
        </span>
        {bustedDownstream && (
          <span className="font-mono text-[10px] text-destructive/80">
            team out 🪦
          </span>
        )}
        {!bustedDownstream && (status === "earned" || status === "pending") && (
          <span className="ml-auto font-mono text-[10px] text-muted-foreground">
            {status === "earned"
              ? `+${pointsEarned} pts`
              : `${pointsPossible} possible`}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {winnerTeam ? (
          <>
            <Flag code={winnerTeam.code} emoji={winnerTeam.flag} size={16} />
            <span
              className={cn(
                "font-display text-sm font-bold",
                bustedDownstream
                  ? "text-muted-foreground line-through"
                  : "text-foreground",
              )}
            >
              {winnerTeam.name}
            </span>
            <RankBadge code={winnerTeam.code} size="xs" />
          </>
        ) : (
          <span className="text-xs italic text-muted-foreground">No winner pick</span>
        )}
        {match.predictedGoals && (
          <span className="ml-auto rounded border border-border bg-background/40 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
            {match.predictedGoals} goals
          </span>
        )}
      </div>
      {loserTeam && (
        <div className="mt-1 flex items-center gap-1.5 pl-[22px] text-[11px] text-muted-foreground">
          <span className="font-mono uppercase tracking-wide">def.</span>
          <Flag code={loserTeam.code} emoji={loserTeam.flag} size={12} />
          <span
            className={cn(
              "font-medium",
              loserDead && "text-destructive line-through",
            )}
          >
            {loserTeam.name}
          </span>
          {loserDead && <Skull className="h-3 w-3 text-destructive" />}
        </div>
      )}

    </div>
  );
}

// Champion path: the 6 matches the user's predicted champion needs to win
// (R32 → R16 → QF → SF → Final). Status comes from the bracket scoring map
// already computed for the tree view.
function ChampionPathWidget({
  matches,
  champion,
  pickResults,
}: {
  matches: Match[];
  champion: { name: string; code: string; flag: string };
  pickResults: Record<string, PickResult>;
}) {
  const ROUND_ORDER: { key: string; label: string; short: string }[] = [
    { key: "Round of 32", label: "Round of 32", short: "R32" },
    { key: "Round of 16", label: "Round of 16", short: "R16" },
    { key: "Quarter Final", label: "Quarter Final", short: "QF" },
    { key: "Semi Final", label: "Semi Final", short: "SF" },
    { key: "Final", label: "Final", short: "F" },
  ];

  const stops = ROUND_ORDER.map((r) => {
    const m = matches.find(
      (mm) =>
        mm.round === r.key &&
        ((mm.winner === "A" && mm.teamA?.code === champion.code) ||
          (mm.winner === "B" && mm.teamB?.code === champion.code)),
    );
    const status: PickResult | "missing" = m
      ? pickResults[m.id] ?? "pending"
      : "missing";
    const opponent =
      m && m.winner === "A" ? m.teamB : m && m.winner === "B" ? m.teamA : null;
    return { round: r, match: m, status, opponent };
  });

  const wins = stops.filter((s) => s.status === "correct").length;
  // "busted" (team eliminated, match still pending in DB) counts as a bust
  // here too — once the picked champion is out, the rest of the path is dead.
  const busted = stops.findIndex(
    (s) => s.status === "incorrect" || s.status === "busted",
  );
  const allDone = wins === 5;

  return (
    <section className="mb-8">
      <SectionHeading
        icon={<Trophy className="h-4 w-4" />}
        title="Champion path"
      />
      <div className="rounded-2xl border border-border bg-card p-4 sm:p-5">
        <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs">
          <span className="font-mono uppercase tracking-wider text-[10px] text-muted-foreground">
            {champion.name} needs:
          </span>
          <span className="font-bold text-foreground">
            {wins} <span className="font-normal text-muted-foreground">won</span>
          </span>
          <span className="text-muted-foreground">/ 5 matches</span>
          {allDone && (
            <span className="text-success font-semibold">· Lifted the cup 🏆</span>
          )}
          {busted >= 0 && (
            <span className="text-destructive font-semibold">
              · Out in {ROUND_ORDER[busted].short} 🪦
            </span>
          )}
        </div>
        <ol className="grid grid-cols-5 gap-1.5 sm:gap-2">
          {stops.map((s, i) => {
            const dim = busted >= 0 && i > busted;
            const cls =
              s.status === "correct"
                ? "border-success/50 bg-success/10 text-success"
                : s.status === "incorrect" || s.status === "busted"
                  ? "border-destructive/50 bg-destructive/10 text-destructive"
                  : s.status === "void"
                    ? "border-muted bg-muted/30 text-muted-foreground"
                    : s.status === "missing"
                      ? "border-dashed border-border bg-muted/10 text-muted-foreground"
                      : "border-border bg-background text-foreground";
            return (
              <li
                key={s.round.key}
                className={cn(
                  "rounded-md border px-1.5 py-2 text-center transition-opacity",
                  cls,
                  dim && "opacity-50",
                )}
                title={
                  s.opponent
                    ? `${s.round.label} vs ${s.opponent.name}`
                    : s.round.label
                }
              >
                <div className="font-mono text-[9px] uppercase tracking-wider opacity-80">
                  {s.round.short}
                </div>
                <div className="mt-1 flex justify-center">
                  {s.status === "correct" ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : s.status === "incorrect" ? (
                    <X className="h-3.5 w-3.5" />
                  ) : s.status === "void" ? (
                    <Minus className="h-3.5 w-3.5" />
                  ) : (
                    <Clock className="h-3.5 w-3.5 opacity-60" />
                  )}
                </div>
                {s.opponent && (
                  <div className="mt-1 truncate text-[9px] uppercase tracking-wide opacity-70">
                    vs {getDisplayCode(s.opponent.code)}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

// =================================================================
// Busted-downstream alert
// =================================================================
// Aggregates pending picks whose chosen team is already eliminated.
// Groups by team so the user sees one line per "dead" pick: e.g.
//   "Brazil out → your QF + SF + F picks involving Brazil go cold"
const ROUND_LABELS_SHORT: Record<string, string> = {
  "Round of 32": "R32",
  "Round of 16": "R16",
  "Quarter Final": "QF",
  "Semi Final": "SF",
  Final: "F",
  "Third Place": "3rd",
};

function BustedPicksAlert({
  matches,
  breakdown,
  eliminatedTeamCodes,
}: {
  matches: Match[];
  breakdown: MemberBracketView["bracketScore"]["breakdown"];
  eliminatedTeamCodes: Set<string>;
}) {
  const breakdownByFrontId = new Map(
    breakdown.map((b) => [matchCodeToFrontId(b.match_code), b]),
  );

  // For each pending pick whose winner team is in the eliminated set,
  // record which round it was picked in, keyed by team code.
  const byTeam = new Map<
    string,
    { team: { code: string; name: string; flag: string }; rounds: string[] }
  >();
  for (const m of matches) {
    const status = breakdownByFrontId.get(m.id)?.status ?? "not_picked";
    if (status !== "pending") continue;
    const winnerTeam =
      m.winner === "A" ? m.teamA : m.winner === "B" ? m.teamB : null;
    if (!winnerTeam) continue;
    if (!eliminatedTeamCodes.has(winnerTeam.code)) continue;
    const short = ROUND_LABELS_SHORT[m.round] ?? m.round;
    const cur = byTeam.get(winnerTeam.code);
    if (cur) {
      cur.rounds.push(short);
    } else {
      byTeam.set(winnerTeam.code, { team: winnerTeam, rounds: [short] });
    }
  }

  if (byTeam.size === 0) return null;

  const entries = Array.from(byTeam.values());

  return (
    <section className="mb-6">
      <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-4 sm:p-5">
        <div className="mb-2 flex items-center gap-2">
          <X className="h-4 w-4 text-destructive" strokeWidth={3} />
          <h3 className="font-display text-sm font-black uppercase tracking-[0.2em] text-destructive">
            Busted picks
          </h3>
          <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-destructive/70">
            {entries.length} team{entries.length === 1 ? "" : "s"} out
          </span>
        </div>
        <ul className="space-y-1.5">
          {entries.map(({ team, rounds }) => (
            <li
              key={team.code}
              className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm"
            >
              <Flag code={team.code} emoji={team.flag} size={14} />
              <span className="font-display font-bold text-foreground line-through">
                {team.name}
              </span>
              <span className="text-muted-foreground">→</span>
              <span className="text-xs text-destructive/90">
                {rounds.join(" + ")} pick{rounds.length === 1 ? "" : "s"} go cold 🪦
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-[11px] italic text-muted-foreground">
          Goblin's noting these for the activity feed. Nothing you can do — the
          bracket is locked. 🧌
        </p>
      </div>
    </section>
  );
}
